import React, { useContext, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import FormElement from './FormElement'
import { ExtendedFormikContext } from 'components/Forms/ExtendedFormik'

import ExtendedInput from './ExtendedInput/ExtendedInput'

const Input = ({
    form,
    field,
    validating,
    loading,
    elementClass,
    displayErrors,
    inlineDescription,
    strongLabel,
    isStatic,
    renderEditButton,
    editDisplayButton,
    editTrackName,
    editOnClick,
    onFocus,
    onBlur,
    onChange,
    size,
    tip,
    unitsTip,
    additionalDetailsButton,
    additionalDetailsButtonText,
    cancelEditButton,
    onCancelEditClick,
    extendedInputProps,
    hideErrorMessage,
    displayUntouchedErrorIfNotEmpty,
    ...props
}) => {
    const ref = useRef(null)

    const { name, value } = field
    const { touched, errors } = form
    const error = displayErrors && !validating && (touched[name] || (displayUntouchedErrorIfNotEmpty && value)) && errors[name]

    const { setFieldValue, handleBlur, handleChange } = useContext(ExtendedFormikContext)

    const _handleBlur = useCallback((e, setFieldValue, handleBlur) => {
        if (!props.type || (props.type && props.type !== 'number')) {
            if (e.target.value.trim() !== e.target.value) {
                setFieldValue(name, e.target.value.trim())
            }
        }
        if (onBlur) {
            onBlur(e)
        } else {
            handleBlur(e)
        }
    }, [props.type, name, onBlur])

    const _handleFocus = useCallback((e) => {
        // we do not want that scrolling over focused number field changes the number up or down
        if (e.target.type === 'number') {
            const stopScroll = (e) => {
                e.preventDefault()
                e.stopPropagation()
                return false
            }

            e.target.addEventListener('mousewheel', stopScroll) // IE, Chrome, Safari, Opera
            e.target.addEventListener('DOMMouseScroll', stopScroll) // Firefox
        }

        if (onFocus) {
            onFocus(e)
        }
    }, [onFocus])

    return (
        <FormElement
            name={name}
            value={value}
            error={error}
            loading={loading}
            validating={validating}
            elementClass={elementClass}
            inlineDescription={inlineDescription}
            strongLabel={strongLabel}
            isStatic={isStatic}
            renderEditButton={renderEditButton}
            editDisplayButton={editDisplayButton}
            editTrackName={editTrackName}
            editOnClick={editOnClick}
            tip={tip}
            additionalDetailsButton={additionalDetailsButton}
            additionalDetailsButtonText={additionalDetailsButtonText}
            cancelEditButton={cancelEditButton}
            onCancelEditClick={onCancelEditClick}
            hideErrorMessage={hideErrorMessage}
            {...props}
        >
            {() => (
                <ExtendedInput
                    size={size}
                    unitsTip={unitsTip}
                    {...extendedInputProps}
                >
                    <input
                        ref={ref}
                        type="text"
                        {...field}
                        {...props}
                        onFocus={e => _handleFocus(e)}
                        onBlur={e => _handleBlur(e, setFieldValue, handleBlur)}
                        onChange={onChange || handleChange}
                    />
                </ExtendedInput>
            )}
        </FormElement>
    )
}

Input.propTypes = {
    label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    extendedInputProps: PropTypes.shape(ExtendedInput.propTypes),
    displayUntouchedErrorIfNotEmpty: PropTypes.bool,
    displayErrors: PropTypes.bool,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    inlineDescription: PropTypes.bool,
    strongLabel: PropTypes.bool,
}

Input.defaultProps = {
    displayErrors: true,
    extendedInputProps: {},
}

export default Input
